import React, { useState, useEffect } from 'react';
import { t } from '../i18n';
import BlueTickBadge from '../components/BlueTickBadge';
import MeritButton, { RankBadge } from '../components/MeritControls';
import { useParams } from 'react-router-dom';


function renderMessageBody(text) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  urlRegex.lastIndex = 0;
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const yt = part.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (yt) return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{display:'block',marginTop:'4px'}}>
          <img src={`https://img.youtube.com/vi/${yt[1]}/mqdefault.jpg`} style={{width:'200px',borderRadius:'8px'}} alt="YouTube"/>
          <div style={{color:'#0F6E56',fontSize:'11px',marginTop:'2px'}}>▶ YouTube Video</div>
        </a>
      );
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{color:'#0F6E56',textDecoration:'underline',wordBreak:'break-all',fontSize:'13px'}}>{part}</a>;
    }
    return <span key={i}>{part}</span>;
  });
}

function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Headings
    if (line.startsWith('# ')) return <h1 key={i} style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: '12px 0 8px' }}>{line.slice(2)}</h1>;
    if (line.startsWith('## ')) return <h2 key={i} style={{ fontSize: '20px', fontWeight: 'bold', color: '#444', margin: '10px 0 6px' }}>{line.slice(3)}</h2>;
    if (line.startsWith('### ')) return <h3 key={i} style={{ fontSize: '17px', fontWeight: 'bold', color: '#555', margin: '8px 0 4px' }}>{line.slice(4)}</h3>;
    // Quote
    if (line.startsWith('> ')) return <blockquote key={i} style={{ borderLeft: '4px solid #0F6E56', paddingLeft: '12px', color: '#666', margin: '8px 0', fontStyle: 'italic' }}>{line.slice(2)}</blockquote>;
    // Divider
    if (line === '---') return <hr key={i} style={{ border: 'none', borderTop: '2px solid #e0e0e0', margin: '12px 0' }} />;
    // List
    if (line.startsWith('- ')) return <li key={i} style={{ marginLeft: '16px', color: '#333', fontSize: '15px' }}>{renderInline(line.slice(2))}</li>;
    // Empty line
    if (line.trim() === '') return <br key={i} />;
    // Normal paragraph
    return <p key={i} style={{ margin: '4px 0', color: '#333', fontSize: '15px', lineHeight: '1.7' }}>{renderInline(line)}</p>;
  });
}

function renderInline(text) {
  if (!text) return null;

  // Ek hi regex, tarteeb ahem hai: code pehle, taake `**x**` code ke
  // andar bold na ban jaye.
  const INLINE_RX = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[[^\]]+\]\([^)]+\))|(https?:\/\/[^\s]+)/g;

  const nodes = [];
  let key = 0;
  let last = 0;
  let m;

  const codeStyle = {
    backgroundColor: '#E1F5EE', padding: '1px 6px', borderRadius: '4px',
    fontFamily: 'monospace', fontSize: '13px',
  };
  const linkStyle = {
    color: '#0F6E56', textDecoration: 'underline', wordBreak: 'break-all',
  };

  while ((m = INLINE_RX.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    }
    const tok = m[0];
    if (m[1]) {
      nodes.push(<code key={key++} style={codeStyle}>{tok.slice(1, -1)}</code>);
    } else if (m[2]) {
      nodes.push(<strong key={key++} style={{ fontWeight: 700 }}>{tok.slice(2, -2)}</strong>);
    } else if (m[3]) {
      nodes.push(<em key={key++} style={{ fontStyle: 'italic' }}>{tok.slice(1, -1)}</em>);
    } else if (m[4]) {
      const mm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      nodes.push(
        <a key={key++} href={mm[2]} target="_blank" rel="noopener noreferrer" style={linkStyle}>{mm[1]}</a>
      );
    } else {
      nodes.push(
        <a key={key++} href={tok} target="_blank" rel="noopener noreferrer" style={linkStyle}>{tok}</a>
      );
    }
    last = m.index + tok.length;
  }

  if (last < text.length) {
    nodes.push(<span key={key++}>{text.slice(last)}</span>);
  }
  return nodes;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${d.getDate()}-${months[d.getMonth()]}-${d.getFullYear()} ${hours}:${minutes} ${ampm}`;
}

function linkify(text) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#0F6E56', wordBreak: 'break-all' }}>{part}</a>;
    }
    return part;
  });
}


const SHARE_BTNS = [
  { bg: '#25D366', label: 'WhatsApp', path: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 16.582c-.252.717-1.493 1.408-2.045 1.494-.523.08-1.175.114-1.896-.119-.437-.138-.997-.322-1.713-.631-3.007-1.298-4.973-4.331-5.123-4.532-.149-.198-1.213-1.613-1.213-3.078 0-1.464.769-2.185 1.042-2.484.272-.298.595-.373.794-.373h.573c.184 0 .434-.07.681.519.254.602.863 2.095.937 2.247.075.152.125.328-.025.527-.149.198-.224.322-.372.496-.149.174-.312.389-.178.636.174.298.772 1.273 1.657 2.062 1.138 1.014 2.099 1.328 2.397 1.478.298.149.473.124.647-.075.174-.198.744-.867.942-1.165.198-.298.397-.249.67-.149.272.099 1.735.817 2.033.966.298.149.497.224.572.348.074.124.074.719-.178 1.436z', getHref: (t,u) => `https://wa.me/?text=${t}%20${u}` },
  { bg: '#000', label: 'X/Twitter', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z', getHref: (t,u) => `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
  { bg: '#0088cc', label: 'Telegram', path: 'M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L6.196 13.9l-2.948-.924c-.64-.203-.654-.64.135-.954l11.566-4.458c.537-.194 1.006.131.945.657z', getHref: (t,u) => `https://t.me/share/url?url=${u}&text=${t}` },
  { bg: '#1877F2', label: 'Facebook', path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z', getHref: (t,u) => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
];

function ShareIcons({ title, url }) {
  const t = encodeURIComponent(`Check out: ${title}`);
  const u = encodeURIComponent(url);
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
      {SHARE_BTNS.map(b => (
        <a key={b.label} href={b.getHref(t,u)} target="_blank" rel="noopener noreferrer" title={b.label}
          style={{ width: 30, height: 30, borderRadius: '50%', background: b.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="white"><path d={b.path} /></svg>
        </a>
      ))}
    </div>
  );
}

function ShareButtons({ title, url }) {
  return (
    <ShareIcons title={title} url={url} />
  );
}

const shareStyles = {
  row: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '15px' },
  label: { color: '#999', fontSize: '13px' },
  btn: (color) => ({
    backgroundColor: color,
    color: 'white',
    padding: '5px 12px',
    borderRadius: '15px',
    textDecoration: 'none',
    fontSize: '12px',
    fontWeight: 'bold',
  }),
};

// Post ki durusti — usi din, aur sirf jab koi like/tip na aaya ho.
// Usool backend tay karta hai (can_edit); yahan sirf dikhaya jata hai,
// aur backend save par dobara khud check karta hai.
function EditPostModal({ post, onSaved, onClose }) {
  const [title, setTitle] = useState(post.title || '');
  const [body, setBody] = useState(post.body || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!title.trim() || !body.trim()) { setErr(t('edit_empty')); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`/api/posts/${post.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${localStorage.getItem('token')}` },
        body: JSON.stringify({ title, body }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'Failed');
      onSaved(d);
      onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const box = { width: '100%', padding: '9px 11px', border: '0.5px solid #B4B2A9', borderRadius: 8, fontSize: 14, marginBottom: 10, fontFamily: 'inherit' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', zIndex: 200, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: '1.3rem', maxWidth: 560, width: '100%', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ fontSize: 17, margin: 0, color: '#2C2C2A' }}>✏️ {t('edit_post')}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#5F5E5A' }}>×</button>
        </div>
        <p style={{ fontSize: 11.5, color: '#5F5E5A', margin: '0 0 14px', lineHeight: 1.5, borderLeft: '2px solid #0F6E56', paddingLeft: 10 }}>
          {t('edit_post_note')}
        </p>
        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: '#2C2C2A' }}>{t('edit_title')}</label>
        <input style={box} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: '#2C2C2A' }}>{t('edit_body')}</label>
        <textarea style={{ ...box, minHeight: 220, resize: 'vertical', lineHeight: 1.6 }} value={body} onChange={(e) => setBody(e.target.value)} />
        {err && <p style={{ fontSize: 12.5, color: '#C0392B', margin: '0 0 10px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={busy}
            style={{ fontSize: 14, padding: '9px 20px', border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {busy ? '…' : t('edit_save')}
          </button>
          <button onClick={onClose}
            style={{ fontSize: 14, padding: '9px 16px', border: '0.5px solid #B4B2A9', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
            {t('edit_cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PostDetail({ user }) {
  const [editing, setEditing] = useState(false);
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [tipAmount, setTipAmount] = useState(0.1);
  const [voting, setVoting] = useState(false);
  const [showVoters, setShowVoters] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [boostAmount, setBoostAmount] = useState(1);
  const [commentText, setCommentText] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [boostRates, setBoostRates] = useState([]);
  const [isBoosted, setIsBoosted] = useState(false);
  const [boostInfo, setBoostInfo] = useState(null);

  const loadPost = (postId) => {
    fetch(`/api/posts/${postId || id}/`)
      .then(res => res.json())
      .then(data => { setPost(data); setLoading(false); });
  };

  useEffect(() => { setPost(null); setLoading(true); loadPost(id); fetchBoostInfo(); }, [id]);

  const handleMentionInput = async (val) => {
    const lastWord = val.split(' ').pop();
    if (lastWord.startsWith('@')) {
      const q = lastWord.slice(1);
      try {
        const headers = user ? { 'Authorization': `Token ${user.token}` } : {};
        
        // @all or @followers - show all connected users
        if (q === 'all' || q === 'followers' || q === '') {
          const res = await fetch(`/api/users/mention/?q=&type=followers`, { headers });
          const data = await res.json();
          // Add @all option at top
          const suggestions = q === '' ? [{ username: 'all', avatar: null, label: '👥 Tag all connections' }, ...data] : data;
          setMentionSuggestions(suggestions);
          setShowMentions(suggestions.length > 0);
        } else {
          const [connRes, allRes] = await Promise.all([
            fetch(`/api/users/mention/?q=${q}&type=followers`, { headers }),
            fetch(`/api/users/search/?q=${q}`)
          ]);
          const connData = await connRes.json();
          const allData = await allRes.json();
          const merged = [...connData];
          allData.forEach(u => { if (!merged.find(m => m.username === u.username)) merged.push(u); });
          setMentionSuggestions(merged.slice(0, 8));
          setShowMentions(merged.length > 0);
        }
      } catch(e) { console.error(e); }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = async (username) => {
    if (username === 'all') {
      // Tag all connections
      try {
        const headers = user ? { 'Authorization': `Token ${user.token}` } : {};
        const res = await fetch(`/api/users/mention/?q=&type=followers`, { headers });
        const data = await res.json();
        const mentions = data.map(u => `@${u.username}`).join(' ');
        const words = comment.split(' ');
        words[words.length - 1] = mentions + ' ';
        setComment(words.join(' '));
      } catch(e) {}
    } else {
      const words = comment.split(' ');
      words[words.length - 1] = `@${username} `;
      setComment(words.join(' '));
    }
    setShowMentions(false);
    setMentionSuggestions([]);
  };

  const handleComment = async () => {
    if (!comment) return;
    try {
      await fetch(`/api/posts/${id}/comments/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
        body: JSON.stringify({ body: commentText || comment })
      });
      setComment('');
      loadPost();
    } catch (err) { console.error(err); }
  };

  const fetchBoostInfo = async () => {
    try {
      const [ratesRes, boostedRes] = await Promise.all([
        fetch('/api/boost/rates/'),
        fetch('/api/posts/boosted/')
      ]);
      const rates = await ratesRes.json();
      const boosted = await boostedRes.json();
      setBoostRates(Array.isArray(rates) ? rates : []);
      const isB = boosted.boosted_post_ids && boosted.boosted_post_ids.includes(parseInt(id));
      setIsBoosted(isB);
      if (isB && boosted.boost_details) {
        const detail = boosted.boost_details.find(b => b.post_id === parseInt(id));
        if (detail) setBoostInfo(detail);
      }
    } catch (err) { console.error(err); }
  };

  const handleVote = async () => {
    if (!user) { alert('Please login to vote!'); return; }
    setVoting(true);
    try {
      await fetch(`/api/posts/${id}/vote/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}` }
      });
      loadPost();
    } catch (err) { console.error(err); }
    setVoting(false);
  };

  const handleTip = async () => {
    if (!window.Pi) { alert('Please open in Pi Browser!'); return; }
    try {
      await window.Pi.authenticate(['username', 'payments'], async (payment) => {
        const txid = payment.transaction ? payment.transaction.txid : null;
        if (!txid) return;
        try {
          await fetch('/api/payments/approve/', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId: payment.identifier })
          });
          await fetch('/api/payments/complete/', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId: payment.identifier, txid, postId: id })
          });
        } catch(e) {}
      });
      let tipDone = false;
      await window.Pi.createPayment({
        amount: tipAmount,
        memo: `Tip for post: ${post.title}`,
        metadata: { postId: id }
      }, {
        onReadyForServerApproval: async (paymentId) => {
          await fetch('/api/payments/approve/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId, amount: tipAmount, postId: id })
          });
        },
        onReadyForServerCompletion: async (paymentId, txid) => {
          if (tipDone) return;
          tipDone = true;
          await fetch('/api/payments/complete/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId, txid, postId: id })
          });
          setSuccessMsg('✅ Tip sent successfully! The author has received your Pi.');
          loadPost();
        },
        onCancel: () => setSuccessMsg(''),
        onError: (err) => console.error(err)
      });
    } catch (err) { console.error(err); }
  };

  if (loading) return <div style={styles.loading}>Loading post...</div>;
  if (!post) return <div style={styles.loading}>Post not found!</div>;

  // Success Modal
  const SuccessModal = () => successMsg ? (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '30px', maxWidth: '320px', width: '90%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: '50px', marginBottom: '12px' }}>🎉</div>
        <h3 style={{ color: '#0F6E56', marginBottom: '10px' }}>Success!</h3>
        <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.5', marginBottom: '20px' }}>{successMsg}</p>
        <button style={{ backgroundColor: '#0F6E56', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', width: '100%' }}
          onClick={() => setSuccessMsg('')}>OK</button>
      </div>
    </div>
  ) : null;

  const hasVoted = user && post.votes.some(v => v.voter.username === user.username);
  const postUrl = `${window.location.origin}/post/${id}`;

  const canModerate = user && (user.username === 'khalidxpert' || user.username === post?.author?.username);

  const handleBoost = async () => {
    if (!user) { alert('Please login first!'); return; }
    if (!window.Pi) { alert('Please open in Pi Browser!'); return; }
    setBoosting(true);
    try {
      await window.Pi.authenticate(['username', 'payments'], async (payment) => {
        const txid = payment.transaction ? payment.transaction.txid : null;
        if (!txid) return;
        const postId = payment.metadata ? payment.metadata.post_id : id;
        try {
          await fetch('/api/payments/approve/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId: payment.identifier })
          });
          await fetch('/api/payments/complete/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId: payment.identifier, txid: txid, postId: postId })
          });
          await fetch(`/api/posts/${postId}/boost/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ amount: payment.amount, payment_id: payment.identifier, txid: txid })
          });
        } catch(e) {}
      });
      let boostDone = false;
      await window.Pi.createPayment({
        amount: boostAmount,
        memo: `Boost post: ${post.title.substring(0, 25)}`,
        metadata: { post_id: id, type: 'boost' }
      }, {
        onReadyForServerApproval: async (paymentId) => {
          await fetch('/api/payments/approve/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId, amount: boostAmount, postId: id })
          });
        },
        onReadyForServerCompletion: async (paymentId, txid) => {
          if (boostDone) return;
          boostDone = true;
          try {
            await fetch('/api/payments/complete/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
              body: JSON.stringify({ paymentId, txid, amount: boostAmount, postId: id })
            });
            const res = await fetch(`/api/posts/${id}/boost/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
              body: JSON.stringify({ amount: boostAmount, payment_id: paymentId, txid })
            });
            const data = await res.json();
            setSuccessMsg('🚀 ' + (data.message || 'Post Boosted Successfully!'));
            setShowBoost(false);
            setBoosting(false);
            fetchBoostInfo();
          } catch(e) {
            console.error('completion error:', e);
            setBoosting(false);
          }
        },
        onCancel: () => { setBoosting(false); },
        onError: (err) => { console.error(err); setBoosting(false); }
      });
    } catch(err) {
      console.error('Boost error:', err);
      setBoosting(false);
    }
  };

  const deletePost = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await fetch(`/api/posts/${id}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${user.token}` }
      });
      window.location.href = '/';
    } catch (err) { console.error(err); }
  };

  // eslint-disable-next-line no-unused-vars
  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await fetch(`/api/comments/${commentId}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${user.token}` }
      });
      loadPost();
    } catch (err) { console.error(err); }
  };

  return (
    <div style={styles.container}>
      <SuccessModal />
      <div style={styles.card}>
        <div style={styles.header}>
          <a href={`/profile/${post.author.username}`} style={styles.author}>
            {post.author.avatar ? (
              <img src={post.author.avatar} alt={post.author.username} style={styles.avatar} />
            ) : '👤 '}
            {post.author.username}{post.author.has_blue_tick && <BlueTickBadge size={14} />}
            <RankBadge username={post.author.username} size={10} />
          </a>
          <span style={styles.date}>{formatDate(post.created_at)}</span>
          {post.can_edit && (
            <button onClick={() => setEditing(true)}
              style={{ ...styles.deleteBtn, background: '#0F6E56', marginRight: 8 }}>
              ✏️ {t('edit_post')}
            </button>
          )}
          {canModerate && (
            <button onClick={deletePost} style={styles.deleteBtn}>🗑️ Delete Post</button>
          )}
        </div>

        <h1 style={styles.title}>
          {isBoosted && <span style={{ backgroundColor: '#0F6E56', color: '#000', fontSize: '12px', padding: '3px 10px', borderRadius: '10px', fontWeight: 'bold', marginRight: '8px', verticalAlign: 'middle' }}>📢 Sponsored</span>}
          {post.title}
        </h1>

        {post.image && <img src={post.image} alt={post.title} style={styles.postImage} />}

        <div style={styles.body}>{renderMarkdown(post.body)}</div>

        <div style={styles.stats}>
          <span style={styles.reward}>💰 {post.pi_reward_pool} Pi Earned</span>
          <button
            style={{ ...styles.voteBtn, backgroundColor: hasVoted ? '#0F6E56' : '#fff', color: hasVoted ? '#fff' : '#0F6E56' }}
            onClick={handleVote}
            disabled={voting}
          >
            👍 {post.total_votes} {hasVoted ? 'Voted' : 'Vote'}
          </button>
          <MeritButton
            postId={post.id}
            authorUsername={post.author.username}
            user={user}
            onDone={loadPost}
          />
          {post.total_votes > 0 && (
            <button style={styles.voterBtn} onClick={() => setShowVoters(!showVoters)}>
              {showVoters ? 'Hide' : 'See voters'}
            </button>
          )}
        </div>

        {showVoters && (
          <div style={styles.voterList}>
            <strong>Voters:</strong>
            <div style={styles.voterRow}>
              {post.votes.map(v => (
                <a key={v.id} href={`/profile/${v.voter.username}`} style={styles.voterChip}>
                  {v.voter.avatar ? <img src={v.voter.avatar} alt={v.voter.username} style={styles.voterAvatar} /> : '👤'}
                  {v.voter.username}
                </a>
              ))}
            </div>
          </div>
        )}

        <ShareIcons title={post.title} url={postUrl} />

        {isBoosted && (
          <div style={{ marginTop: '12px', backgroundColor: '#E1F5EE', border: '2px solid #0F6E56', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '20px' }}>📢</span>
            <div>
              <div style={{ fontWeight: 'bold', color: '#0d9488', fontSize: '14px' }}>Post is Boosted (Sponsored)</div>
              {boostInfo && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                  💰 {boostInfo.amount} Pi paid · 📅 {boostInfo.days_left} days remaining · Expires: {boostInfo.expires_at}
                </div>
              )}
            </div>
          </div>
        )}

        {user && (user.username === post.author.username || user.username === 'khalidxpert') && (
          <div style={{ marginTop: '15px' }}>
            <button
              style={{ backgroundColor: isBoosted ? '#10b981' : '#0F6E56', color: isBoosted ? 'white' : '#000', border: 'none', padding: '8px 18px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
              onClick={() => setShowBoost(!showBoost)}
            >{isBoosted ? '🚀 Boost Again' : '🚀 Boost Post'}</button>
            {showBoost && (
              <div style={{ marginTop: '10px', backgroundColor: '#E1F5EE', padding: '15px', borderRadius: '12px', border: '2px solid #0F6E56' }}>
                <p style={{ fontWeight: 'bold', color: '#333', marginBottom: '10px' }}>Select Boost Duration:</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {boostRates.length > 0 ? boostRates.map(rate => (
                    <button key={rate.id}
                      style={{ padding: '8px 14px', borderRadius: '15px', border: '2px solid #0F6E56', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: boostAmount === parseFloat(rate.pi_amount) ? '#0F6E56' : 'white', color: '#333' }}
                      onClick={() => setBoostAmount(parseFloat(rate.pi_amount))}
                    >{rate.pi_amount} Pi — {rate.label}</button>
                  )) : [{pi: 1, label: '1 Day'}, {pi: 5, label: '7 Days'}, {pi: 10, label: '30 Days'}].map(opt => (
                    <button key={opt.pi}
                      style={{ padding: '8px 14px', borderRadius: '15px', border: '2px solid #0F6E56', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: boostAmount === opt.pi ? '#0F6E56' : 'white', color: '#333' }}
                      onClick={() => setBoostAmount(opt.pi)}
                    >{opt.pi} Pi — {opt.label}</button>
                  ))}
                </div>
                <button
                  style={{ backgroundColor: '#0F6E56', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', width: '100%' }}
                  onClick={handleBoost}
                  disabled={boosting}
                >{boosting ? 'Boosting...' : `🚀 Boost for ${boostAmount} Pi`}</button>
              </div>
            )}
          </div>
        )}

        {user && (
          <div style={styles.tipBox}>
            <h3>💰 Tip Author with Pi</h3>
            <div style={styles.tipRow}>
              <select style={styles.select} value={tipAmount} onChange={e => setTipAmount(Number(e.target.value))}>
                <option value={0.1}>0.1 Pi</option>
                <option value={0.5}>0.5 Pi</option>
                <option value={1}>1 Pi</option>
                <option value={5}>5 Pi</option>
              </select>
              <button style={styles.tipBtn} onClick={handleTip}>Send Tip 🚀</button>
            </div>
          </div>
        )}
      </div>

      <div style={styles.commentsSection}>
        <h2>💬 Comments ({post.comments.length})</h2>
        {user && (
          <div style={styles.commentBox}>
            <div style={{ position: 'relative' }}>
            <textarea
              style={styles.textarea}
              placeholder="Write a comment... @username to mention"
              value={comment}
              onChange={e => { setComment(e.target.value); handleMentionInput(e.target.value); }}
              rows={3}
            />
            {showMentions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', maxHeight: '150px', overflowY: 'auto' }}>
                {mentionSuggestions.map(u => (
                  <div key={u.username} onClick={() => insertMention(u.username)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                    {u.label
                      ? <span style={{ fontSize: '18px' }}>👥</span>
                      : u.avatar
                        ? <img src={u.avatar} alt={u.username} style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                        : <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#0F6E56', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>{u.username[0].toUpperCase()}</div>
                    }
                    <span style={{ color: '#333', fontSize: '14px' }}>{u.label || `@${u.username}`}</span>
                  </div>
                ))}
              </div>
            )}
            </div>
            <button style={styles.commentBtn} onClick={handleComment}>Post Comment</button>
          </div>
        )}
        {post.comments.map(c => (
          <div key={c.id} style={styles.comment}>
            <a href={`/profile/${c.author.username}`} style={styles.commentAuthor}>
              {c.author.avatar ? <img src={c.author.avatar} alt={c.author.username} style={styles.avatarSmall} /> : '👤 '}
              {c.author.username}{c.author.has_blue_tick && <BlueTickBadge size={12} />}
              <RankBadge username={c.author.username} size={9} />
            </a>
            <p style={styles.commentBody}>{c.body}</p>
            <span style={styles.commentDate}>{formatDate(c.created_at)}</span>
          </div>
        ))}
      </div>
      {editing && (
        <EditPostModal post={post} onClose={() => setEditing(false)} onSaved={(d) => setPost(d)} />
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '800px', margin: '0 auto', padding: '20px' },
  card: { backgroundColor: 'white', borderRadius: '10px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' },
  author: { color: '#0F6E56', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' },
  avatar: { width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' },
  avatarSmall: { width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginRight: '4px', verticalAlign: 'middle' },
  date: { color: '#999' },
  title: { color: '#333', marginBottom: '20px' },
  postImage: { width: '100%', maxHeight: '500px', objectFit: 'cover', borderRadius: '10px', marginBottom: '20px' },
  body: { color: '#555', lineHeight: '1.8', fontSize: '16px', marginBottom: '20px', whiteSpace: 'pre-wrap' },
  stats: { display: 'flex', gap: '15px', alignItems: 'center', padding: '15px 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee', marginBottom: '15px', flexWrap: 'wrap' },
  reward: { color: '#0F6E56', fontWeight: 'bold' },
  voteBtn: { border: '2px solid #0F6E56', padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold' },
  deleteBtn: { marginLeft: 'auto', backgroundColor: '#ff4444', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '15px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  deleteCommentBtn: { marginLeft: '8px', backgroundColor: 'transparent', color: '#ff4444', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '0' },
  voterBtn: { background: 'none', border: 'none', color: '#0F6E56', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' },
  voterList: { backgroundColor: '#f9f9f9', borderRadius: '8px', padding: '12px', marginBottom: '15px' },
  voterRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' },
  voterChip: { display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#E1F5EE', color: '#0F6E56', padding: '4px 10px', borderRadius: '15px', textDecoration: 'none', fontSize: '13px' },
  voterAvatar: { width: '18px', height: '18px', borderRadius: '50%', objectFit: 'cover' },
  tipBox: { backgroundColor: '#E1F5EE', padding: '20px', borderRadius: '10px', marginTop: '15px' },
  tipRow: { display: 'flex', gap: '10px', marginTop: '10px' },
  select: { padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px' },
  tipBtn: { backgroundColor: '#0F6E56', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold' },
  commentsSection: { backgroundColor: 'white', borderRadius: '10px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  commentBox: { marginBottom: '20px' },
  textarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '10px', boxSizing: 'border-box', fontSize: '14px' },
  commentBtn: { backgroundColor: '#0F6E56', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '20px', cursor: 'pointer' },
  comment: { borderBottom: '1px solid #eee', padding: '15px 0' },
  commentAuthor: { color: '#0F6E56', fontWeight: 'bold', display: 'flex', alignItems: 'center', textDecoration: 'none' },
  commentBody: { color: '#555', margin: '8px 0' },
  commentDate: { color: '#999', fontSize: '12px' },
  loading: { textAlign: 'center', padding: '50px', fontSize: '20px' },
};

export default PostDetail;
